const NEXASTAY_ROOT_DOMAIN = 'roomnexa.com';
const RESERVED_SUBDOMAINS = new Set(['www', 'api', 'console', 'admin', 'hotel']);

/** First URL segment on console.roomnexa.com that are staff app routes, not NexaStay hotel ids. */
export const CONSOLE_NEXASTAY_RESERVED_SEGMENTS = new Set([
  'admin',
  'api',
  'booking',
  'channelhub',
  'configuration',
  'customerhub',
  'dashboard',
  'distribution',
  'dynamic-pricing',
  'finance',
  'free-trial',
  'hall',
  'housekeeping',
  'inventory',
  'login',
  'logout',
  'media',
  'nexastay',
  'onboarding',
  'password-reset',
  'profile',
  'restaurant',
  'pos-restaurant',
  'stock',
  'static',
  'uploads',
  'users',
  // Guest web check-in (must not be treated as /<hotel-slug>/ failover)
  'web-checkin',
]);

function getHostname() {
  if (typeof window === 'undefined') return '';
  return (window.location.hostname || '').toLowerCase();
}

export function getNexaStayHostSlug() {
  const hostname = getHostname();
  if (!hostname || !hostname.endsWith(`.${NEXASTAY_ROOT_DOMAIN}`)) return '';
  const subdomain = hostname.slice(0, -(`.${NEXASTAY_ROOT_DOMAIN}`).length);
  if (!subdomain || subdomain.includes('.') || RESERVED_SUBDOMAINS.has(subdomain)) return '';
  return subdomain;
}

export function isNexaStayConsoleFallbackHost() {
  return getHostname() === `console.${NEXASTAY_ROOT_DOMAIN}`;
}

export function isNexaStaySlugDomainHost() {
  return !!getNexaStayHostSlug();
}

/** Public NexaStay booking site on slug CNAME hosts only (not console — console is staff + failover). */
export function isNexaStayPublicHost() {
  return isNexaStaySlugDomainHost();
}

export function isConsoleNexaStayHotelSegment(segment) {
  const normalized = String(segment || '').trim().toLowerCase();
  if (!normalized) return false;
  return !CONSOLE_NEXASTAY_RESERVED_SEGMENTS.has(normalized);
}

export function getConsolePathFirstSegment(pathname) {
  return String(pathname || '/')
    .split('/')
    .filter(Boolean)[0]
    ?.toLowerCase() || '';
}

export function shouldRenderConsoleNexaStayFallback(pathname) {
  if (!isNexaStayConsoleFallbackHost()) return false;
  const segment = getConsolePathFirstSegment(pathname);
  return Boolean(segment) && isConsoleNexaStayHotelSegment(segment);
}

/** NexaStay guest pages that must never use staff MainLayout auth. */
export const NEXASTAY_PUBLIC_PATHS = new Set([
  '/',
  '/about',
  '/contact',
  '/profile',
  '/bookings',
  '/terms',
  '/checkout',
]);

export function isNexaStayPublicPathname(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (NEXASTAY_PUBLIC_PATHS.has(path)) return true;
  if (path.startsWith('/nexastay/hotel/')) return true;
  if (isNexaStayConsoleFallbackHost()) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 1 && isConsoleNexaStayHotelSegment(parts[0])) return true;
    if (
      parts.length === 2
      && isConsoleNexaStayHotelSegment(parts[0])
      && NEXASTAY_PUBLIC_PATHS.has(`/${parts[1]}`)
    ) {
      return true;
    }
  }
  return false;
}

/** Safe in-app redirect target for /login/ on NexaStay public hosts (never staff routes). */
export function resolveNexaStayPublicLoginRedirect(nextParam) {
  const raw = String(nextParam || '').trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  const pathOnly = raw.split('?')[0].replace(/\/+$/, '') || '/';
  if (isNexaStayPublicPathname(pathOnly)) return raw;
  return '/';
}

/**
 * Base path for NexaStay links:
 * - On slug domain (<slug>.roomnexa.com): '' so routes are '/', '/about', ...
 * - On console fallback host (console.roomnexa.com): '' so routes are '/:hotelId/...'
 * - On other hosts: '/nexastay/hotel'
 */
export function useNexaStayBasePath() {
  if (typeof window === 'undefined') return '/nexastay/hotel';
  return isNexaStayConsoleFallbackHost() || isNexaStaySlugDomainHost() ? '' : '/nexastay/hotel';
}

export function resolveNexaStayHotelId(routeHotelId) {
  return routeHotelId || getNexaStayHostSlug() || '';
}

/**
 * Build full path for a NexaStay route (home, about, contact, checkout).
 * @param {string} hotelId - hotel id or slug
 * @param {string} [subPath] - optional: 'about', 'contact', 'checkout'
 */
export function nexastayPath(basePath, hotelId, subPath) {
  if (isNexaStaySlugDomainHost()) {
    return subPath ? `/${subPath}` : '/';
  }
  if (!hotelId) return basePath ? '/nexastay/hotel/2' : '/2';
  const prefix = basePath ? `${basePath}/${hotelId}` : `/${hotelId}`;
  return subPath ? `${prefix}/${subPath}` : prefix;
}
